<script setup lang="ts">
import { RouteName } from '@/router/models/route-name';
import { toProjectDisplayName } from '@/utils/projects/toProjectDisplayName';
import { fmtCurrency } from 'cesg-shared';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { ColumnDef } from './CESGTable.vue';
import CESGTable from './CESGTable.vue';
import type { FailedItem } from './InvoiceProcessingPanel.vue';

const router = useRouter();
const { t } = useI18n();

export interface InvoiceProcessingResultsInvoice
  extends Record<string, unknown> {
  id: string;
  project: {
    id: string;
    name: string;
    fortnoxId: string;
  };
  accountType: string;
  invoiceNumber: string;
  status: string;
  supplier: string;
  totalAmount: number;
  pennyAdjust: number;
  vat: number;
  invoiceDate: string;
  dueDate: string;
  approver: string;
  supplierInvoiceNumber: string;
  booked: boolean;
  paymentFileId: number | null;
  finalPayDate: string | null;
}

const columns: ColumnDef<InvoiceProcessingResultsInvoice>[] = [
  {
    key: 'supplierInvoiceNumber',
    label: t('adminSupplierInvoice.tableHeaders.serialNumber'),
    sortable: true,
    render: (row) => row.supplierInvoiceNumber,
  },
  {
    key: 'project',
    label: t('adminSupplierInvoice.tableHeaders.project'),
    sortable: true,
    render: (row) => toProjectDisplayName(row.project),
  },
  {
    key: 'accountType',
    label: t('adminSupplierInvoice.tableHeaders.accountType'),
    sortable: true,
    render: (row) => row.accountType,
  },
  {
    key: 'totalAmount',
    label: t('adminSupplierInvoice.tableHeaders.totalAmount'),
    sortable: true,
    render: (row) => fmtCurrency(row.totalAmount),
    compareValue: (row) => fmtCurrency(row.totalAmount),
  },
  {
    key: 'vat',
    label: t('adminSupplierInvoice.tableHeaders.vat'),
    sortable: true,
    render: (row) => row.vat.toString(),
  },
  {
    key: 'invoiceDate',
    label: t('adminSupplierInvoice.tableHeaders.invoiceDate'),
    sortable: true,
    render: (row) => row.invoiceDate,
    compareValue(row) {
      return new Date(row.invoiceDate).getTime();
    },
  },
  {
    key: 'dueDate',
    label: t('adminSupplierInvoice.tableHeaders.dueDate'),
    sortable: true,
    render: (row) => row.dueDate,
    compareValue(row) {
      return new Date(row.dueDate).getTime();
    },
  },
  {
    key: 'paymentFileId',
    label: t('adminSupplierInvoice.tableHeaders.paymentFileId'),
    sortable: true,
    render: (row) => row.paymentFileId?.toString() ?? '-',
  },
] as const;

const props = defineProps<{
  processedCount: number;
  failedItems: FailedItem[];
  failedInvoices: InvoiceProcessingResultsInvoice[];
  succeededInvoices: InvoiceProcessingResultsInvoice[];
  retryingIds: Set<string>;
  isRetrying: boolean;
}>();

defineEmits<{
  (e: 'retryOne', id: string): void;
  (e: 'clearAllErrors'): void;
  (e: 'proceedToNextStage'): void;
}>();

const getErrorMessageFor = (id: string): string | undefined => {
  const failedItem = props.failedItems.find((i) => i.id === id);
  if (!failedItem) return;
  return failedItem.errorMessages;
};

const navigateToInvoice = (invoice: InvoiceProcessingResultsInvoice) => {
  router.push({
    name: RouteName.AdminEditInvoice,
    params: { id: invoice.id },
  });
};
</script>
<template>
  <!-- <div v-if="!isProcessing && processedCount > 0" class="results-panel"> -->
  <div v-if="processedCount > 0" class="results-panel">
    <div v-if="failedItems.length === 0" class="results-success">
      <span class="success-message">
        ✓ {{ t('adminSupplierInvoice.bookingInvoices.all') }}
        {{ processedCount }}
        {{ t('adminSupplierInvoice.bookingInvoices.invoicesBooked') }}.
      </span>
      <CESGTable
        :isShowHeader="true"
        :isSelectable="false"
        :columns="columns"
        :rows="succeededInvoices"
        @navigateToItem="navigateToInvoice"
      />
      <button class="btn-proceed" @click="$emit('proceedToNextStage')">
        {{ t('adminSupplierInvoice.bookingInvoices.proceedToNextStage') }}
      </button>
    </div>
    <div v-else class="results-partial">
      <span class="results-warning">
        ⚠ {{ processedCount - failedItems.length }}
        {{ t('adminSupplierInvoice.bookingInvoices.successfulBookings') }},
        {{ failedItems.length }}
        {{ t('adminSupplierInvoice.bookingInvoices.failures') }}.
      </span>
      <div class="failed-list">
        <CESGTable
          :isShowHeader="true"
          :isSelectable="false"
          :columns="columns"
          :rows="failedInvoices"
          @navigateToItem="navigateToInvoice"
        >
          <template #subColumn="{ item }">
            <button
              v-if="item"
              class="btn-retry"
              :disabled="retryingIds.has(item.id)"
              @click="$emit('retryOne', item.id)"
            >
              {{
                retryingIds.has(item.id)
                  ? '...'
                  : t('adminSupplierInvoice.bookingInvoices.tryAgain')
              }}
            </button>
          </template>
          <template #subRow="{ item }">
            <div v-if="item" class="error-sub-row">
              <span class="error-message">{{
                getErrorMessageFor(item.id)
              }}</span>
            </div>
          </template>
        </CESGTable>
      </div>
    </div>
    <!-- v-if="!isProcessing && failedItems.length > 0" -->
    <button
      class="btn-retry btn-clear"
      :disabled="isRetrying"
      v-if="failedItems.length > 0"
      @click="$emit('clearAllErrors')"
    >
      {{ t('adminSupplierInvoice.bookingInvoices.clear') }}
    </button>
  </div>
</template>
<style lang="scss" scoped>
@use '@/assets/style/variables';
@use '@/assets/style/invoice';
@use 'bulma/sass/utilities/initial-variables' as *;

.results-panel {
  margin-top: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: var(--table-row);
  font-size: 0.88rem;

  .failed-list {
    margin-top: 8px;
  }

  .btn-retry {
    flex-shrink: 0;
    padding: 3px 10px;
    border-radius: 4px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid #b45309;
    background-color: #fef3c7;
    color: #b45309;
    transition: all 0.2s;

    &.btn-clear {
      background-color: var(--light-grey);
      margin-top: 1rem;
    }

    &:not(:disabled):hover {
      background-color: #fde68a;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .error-sub-row {
    display: flex;
    .error-message {
      color: var(--text-danger);
    }
  }

  .results-partial {
    .results-warning {
      font-weight: 600;
      color: #b45309;
    }
  }

  .results-success {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .success-message {
      color: #15803d;
      font-weight: 600;
    }
  }

  .btn-proceed {
    align-self: flex-end;
    padding: 7px 18px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.88rem;
    cursor: pointer;
    background-color: #15803d;
    color: white;
    border: 1px solid transparent;
    transition: all 0.2s;

    &:hover {
      box-shadow: 0 0 0 1.5px white inset;
    }
  }
}
</style>
